import type { CreateProfessionInput } from '@idc-forms/schemas'
import { CircleOff, CirclePlay, Info, Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Permission } from '@/features/roles/consts/permissions'
import { DetailFieldRow } from '@/shared/components/detail-rows/detail-field-row'
import { UserChip } from '@/shared/components/detail-rows/user-chip'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/accordion'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shared/ui/alert-dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { formatHumanDateTime } from '@/shared/utils/format-date'
import {
  useDeleteProfessionV2Mutation,
  useUpdateProfessionV2Mutation
} from '../hooks/use-profession-v2-mutations'
import type { ProfessionV2AdminRow } from '../models/profession-admin-v2'
import { ProfessionForm } from './profession-form'
import { ProfessionActiveBadge, ProfessionOtherBadge } from './professions-admin-columns'

type ProfessionV2DetailPanelProps = {
  profession: ProfessionV2AdminRow
}

/**
 * Panel de detalle de la profesión contra la API v2. Espeja el panel v1 —mismo
 * `ProfessionForm`, mismos badges, mismo acordeón de actividad— y sólo cambia el
 * transporte: direcciona por `prf_` y la autoría llega como `{ id: usr_, fullname }`.
 *
 * La fila especial «Otro» sigue siendo de solo lectura: el backend rechaza editarla
 * (`ERROR_PROFESSION_NOT_UPDATE_OTHER`) y borrarla
 * (`ERROR_PROFESSION_NOT_DELETE_OTHER`); la UI simplemente no ofrece las acciones.
 */
export function ProfessionV2DetailPanel({ profession }: ProfessionV2DetailPanelProps) {
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const closePanel = useSidePanelStore((s) => s.closePanel)
  const updateMutation = useUpdateProfessionV2Mutation()
  const deleteMutation = useDeleteProfessionV2Mutation()
  const { has } = usePermissions()

  const isSystemRow = profession.isOther

  const handleUpdate = (values: CreateProfessionInput) => {
    updateMutation.mutate(
      { id: profession.id, body: values },
      {
        onSuccess: () => {
          setMode('view')
        }
      }
    )
  }

  const toggleActive = () => {
    updateMutation.mutate({ id: profession.id, body: { active: !profession.active } })
  }

  if (mode === 'edit') {
    return (
      <ProfessionForm
        defaultValues={{
          name: profession.name,
          active: profession.active
        }}
        submitLabel="Guardar cambios"
        pending={updateMutation.isPending}
        onSubmit={handleUpdate}
        onCancel={() => {
          setMode('view')
        }}
      />
    )
  }

  return (
    <div className="flex flex-col">
      <SidePanelSection>
        <SidePanelSectionBody className="gap-3">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold leading-tight">{profession.name}</h3>
            {isSystemRow && (
              <p className="mt-1 text-sm text-muted-foreground">
                Fila del sistema: es el fallback de texto libre de los formularios KYC.
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <DetailFieldRow
              label="Estado"
              value={<ProfessionActiveBadge active={profession.active} />}
            />
            {isSystemRow && <DetailFieldRow label="Tipo" value={<ProfessionOtherBadge />} />}
            <DetailFieldRow label="Nombre" value={profession.name} copyValue={profession.name} />
          </div>
        </SidePanelSectionBody>
      </SidePanelSection>

      {!isSystemRow && (
        <SidePanelSection>
          <SidePanelActions
            maxInline={2}
            actions={[
              ...(has(Permission.UpdateProfessions)
                ? [
                    {
                      label: 'Editar profesión',
                      icon: <Pencil className="h-4 w-4" />,
                      variant: 'default' as const,
                      primary: true,
                      onClick: () => {
                        setMode('edit')
                      }
                    },
                    {
                      label: profession.active ? 'Desactivar profesión' : 'Activar profesión',
                      icon: profession.active ? (
                        <CircleOff className="h-4 w-4" />
                      ) : (
                        <CirclePlay className="h-4 w-4" />
                      ),
                      variant: 'outline' as const,
                      disabled: updateMutation.isPending,
                      onClick: toggleActive
                    }
                  ]
                : []),
              ...(has(Permission.DeleteProfessions)
                ? [
                    {
                      label: 'Eliminar profesión',
                      icon: <Trash2 className="h-4 w-4" />,
                      variant: 'destructive' as const,
                      disabled: deleteMutation.isPending,
                      onClick: () => {
                        setConfirmDelete(true)
                      }
                    }
                  ]
                : [])
            ]}
          />
        </SidePanelSection>
      )}

      <Tabs defaultValue="more-info">
        <TabsList className="w-full justify-normal rounded-none">
          <TabsTrigger className="flex gap-1" value="more-info">
            <Info className="size-4" /> Más información
          </TabsTrigger>
        </TabsList>
        <TabsContent value="more-info">
          <Accordion type="multiple" defaultValue={['activity']}>
            <AccordionItem value="activity">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Actividad
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow
                    label="Creado"
                    value={formatHumanDateTime(profession.createdAt)}
                  />
                  <DetailFieldRow
                    label="Creado por"
                    value={<UserChip name={profession.createdBy?.fullname} />}
                  />
                  <DetailFieldRow
                    label="Actualizado"
                    value={formatHumanDateTime(profession.updatedAt)}
                  />
                  <DetailFieldRow
                    label="Actualizado por"
                    value={<UserChip name={profession.updatedBy?.fullname} />}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar profesión</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará “{profession.name}” del catálogo. Los registros históricos que la
              referencian se conservan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteMutation.mutate(profession.id, {
                  onSuccess: () => {
                    closePanel()
                  }
                })
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
